
import { resolveReturnUrl } from './return-url';
import { signal, type Signal } from '@angular/core';
import {
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import {
  normalizeMessengerRoles,
  type MessengerAccount,
  type MessengerAuth,
  type MessengerAuthStatus,
  type MessengerRole,
  type MessengerRuntimeConfig,
} from '@crm/messenger';

export const MESSENGER_RETURN_URL_KEY = 'mello-messenger.return-url';

export interface MessengerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface MsalClientLike {
  initialize(): Promise<unknown>;
  handleRedirectPromise(): Promise<{ account?: AccountInfo | null } | null>;
  getActiveAccount(): AccountInfo | null;
  getAllAccounts(): AccountInfo[];
  setActiveAccount(account: AccountInfo | null): void;
  acquireTokenSilent(request: {
    account: AccountInfo;
    scopes: string[];
  }): Promise<{ accessToken: string }>;
  acquireTokenRedirect(request: {
    account?: AccountInfo;
    scopes: string[];
  }): Promise<unknown>;
  loginRedirect(request: { scopes: string[]; prompt?: string }): Promise<unknown>;
  logoutRedirect(request: { account?: AccountInfo }): Promise<unknown>;
}

export interface MsalMessengerAuthOptions {
  readonly config: () => MessengerRuntimeConfig;
  readonly clientFactory?: (
    config: MessengerRuntimeConfig,
  ) => MsalClientLike;
  readonly storage?: MessengerStorage;
}

function defaultStorage(): MessengerStorage | undefined {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

export class MsalMessengerAuth implements MessengerAuth {
  private readonly _status = signal<MessengerAuthStatus>('initializing');
  private readonly _account = signal<MessengerAccount | null>(null);
  private readonly _roles = signal<readonly MessengerRole[]>([]);
  private readonly _error = signal('');

  readonly status: Signal<MessengerAuthStatus> = this._status.asReadonly();
  readonly account: Signal<MessengerAccount | null> =
    this._account.asReadonly();
  readonly roles: Signal<readonly MessengerRole[]> = this._roles.asReadonly();
  readonly error: Signal<string> = this._error.asReadonly();

  private client: MsalClientLike | null = null;
  private rawAccount: AccountInfo | null = null;
  private initialization: Promise<void> | null = null;
  private readonly storage?: MessengerStorage;

  constructor(private readonly options: MsalMessengerAuthOptions) {
    this.storage = options.storage ?? defaultStorage();
  }

  async initialize(): Promise<void> {
    const config = this.options.config();
    if (config.mode !== 'remote') {
      this._status.set('disabled');
      this._account.set(null);
      this._roles.set([]);
      return Promise.resolve();
    }
    if (this.initialization) return this.initialization;

    this._status.set('initializing');
    this.initialization = (async () => {
      const client = this.createClient(config);
      await client.initialize();
      const redirect = await client.handleRedirectPromise();
      const account =
        redirect?.account ?? client.getActiveAccount() ?? client.getAllAccounts()[0] ?? null;
      this.rawAccount = account;
      if (account) client.setActiveAccount(account);
      this.publish(account);
      this.client = client;
    })();

    try {
      return await this.initialization;
    } catch (error) {
      this.client = null;
      this.initialization = null;
      this._status.set('error');
      this._error.set('Authentication could not be initialized.');
      throw error;
    }
  }

  async accessToken(scopes?: readonly string[]): Promise<string> {
    const config = this.options.config();
    await this.initialize();
    if (config.mode !== 'remote' || !this.client) return '';
    if (this._status() === 'redirecting') return '';
    if (!this.rawAccount) {
      await this.signIn();
      return '';
    }
    const effectiveScopes = scopes && scopes.length > 0 ? [...scopes] : [...config.scopes];
    try {
      const token = await this.client.acquireTokenSilent({
        account: this.rawAccount,
        scopes: effectiveScopes,
      });
      if (!scopes || scopes.length === 0) {
        this.mergeAccessTokenRoles(token.accessToken);
      }
      return token.accessToken;
    } catch (error) {
      if (isInteractionRequired(error)) {
        // Only trigger full-page redirect for primary API scopes; optional Graph scopes fail closed so the page doesn't redirect-loop.
        if (scopes && scopes.length > 0) {
          return '';
        }
        this._status.set('redirecting');
        this.rememberReturnUrl();
        await this.client.acquireTokenRedirect({
          account: this.rawAccount,
          scopes: effectiveScopes,
        });
        return '';
      }
      if (scopes && scopes.length > 0) {
        return '';
      }
      this._status.set('signed-out');
      this._error.set('Sign-in is required to continue.');
      return '';
    }
  }

  async signIn(returnUrl?: string): Promise<void> {
    const config = this.options.config();
    await this.initialize();
    if (config.mode !== 'remote' || !this.client || this._status() === 'redirecting') return;
    this._status.set('redirecting');
    this.rememberReturnUrl(returnUrl);
    await this.client.loginRedirect({
      scopes: [...config.scopes],
      prompt: 'select_account',
    });
  }

  async signOut(): Promise<void> {
    const config = this.options.config();
    await this.initialize();
    if (config.mode !== 'remote' || !this.client || !this.rawAccount) return;
    this._status.set('redirecting');
    await this.client.logoutRedirect({ account: this.rawAccount });
  }

  consumeReturnUrl(): string | null {
    const url = this.storage?.getItem(MESSENGER_RETURN_URL_KEY) ?? null;
    if (url) this.storage?.removeItem(MESSENGER_RETURN_URL_KEY);
    return url;
  }

  private createClient(config: MessengerRuntimeConfig): MsalClientLike {
    if (this.options.clientFactory) return this.options.clientFactory(config);
    return new PublicClientApplication({
      auth: {
        clientId: config.msal.clientId,
        authority: config.msal.authority,
        knownAuthorities: [...config.msal.knownAuthorities],
        redirectUri: resolveUri(config.msal.redirectUri),
        postLogoutRedirectUri: resolveUri(config.msal.postLogoutRedirectUri),
      },
      cache: { cacheLocation: config.msal.cacheLocation },
    }) as MsalClientLike;
  }

  private publish(account: AccountInfo | null, extraClaims: readonly string[] = []): void {
    this._account.set(account ? accountSnapshot(account, extraClaims) : null);
    const idClaims = (account?.idTokenClaims?.['roles'] as unknown[]) ?? [];
    this._roles.set(normalizeMessengerRoles([...idClaims, ...extraClaims]));
    this._status.set(account ? 'authenticated' : 'signed-out');
    this._error.set('');
  }

  private mergeAccessTokenRoles(jwt: string): void {
    if (!this.rawAccount || !jwt) return;
    const tokenRoles = extractJwtRoleClaims(jwt);
    if (tokenRoles.length === 0) return;
    const current = this._account()?.claimRoles ?? [];
    const merged = [...new Set([...current, ...tokenRoles])];
    if (merged.length !== current.length) {
      this.publish(this.rawAccount, tokenRoles);
    }
  }

  private rememberReturnUrl(returnUrl?: string): void {
    const url = resolveReturnUrl(
      returnUrl ??
        (typeof location !== 'undefined'
          ? `${location.pathname}${location.search}`
          : undefined),
    );
    try {
      this.storage?.setItem(MESSENGER_RETURN_URL_KEY, url);
    } catch {
      return;
    }
  }
}

const INTERACTION_REQUIRED_CODES = new Set([
  'interaction_required',
  'consent_required',
  'login_required',
  'no_tokens_found',
  'invalid_grant',
  'token_renewal_error',
  'monitor_window_timeout',
  'timed_out',
]);

function isInteractionRequired(error: unknown): boolean {
  const record =
    error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const errorCode =
    typeof record['errorCode'] === 'string' ? record['errorCode'] : '';
  if (errorCode && errorCode !== 'interaction_in_progress') {
    return true;
  }
  const code =
    typeof record['name'] === 'string' ? record['name'] : '';
  return (
    INTERACTION_REQUIRED_CODES.has(code) ||
    code === 'InteractionRequiredAuthError'
  );
}

function resolveUri(uri: string): string {
  if (typeof location === 'undefined') return uri;
  try {
    const parsed = new URL(uri, location.origin);
    const configuredIsLocal = ['localhost', '127.0.0.1'].includes(
      parsed.hostname,
    );
    const currentIsLocal = ['localhost', '127.0.0.1'].includes(
      location.hostname,
    );
    if (configuredIsLocal && !currentIsLocal) {
      return `${location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.href;
  } catch {
    return uri;
  }
}

function accountSnapshot(
  account: AccountInfo,
  extraClaims: readonly string[] = [],
): MessengerAccount {
  const idClaims = (account.idTokenClaims?.['roles'] as unknown[]) ?? [];
  const allClaims = [
    ...idClaims.filter((claim): claim is string => typeof claim === 'string'),
    ...extraClaims,
  ];
  const uniqueClaims = [...new Set(allClaims)];
  return {
    name: account.name || account.username || 'Signed-in user',
    email: account.username || '',
    tenantId: account.tenantId || '',
    roles: normalizeMessengerRoles(uniqueClaims),
    claimRoles: uniqueClaims,
  };
}

function extractJwtRoleClaims(jwt: string): string[] {
  try {
    if (typeof atob !== 'function') return [];
    const parts = jwt.split('.');
    if (parts.length < 2 || !parts[1]) return [];
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const json = atob(padded);
    const payload = JSON.parse(json) as Record<string, unknown>;
    const roles = payload['roles'];
    if (!Array.isArray(roles)) return [];
    return roles.filter((r): r is string => typeof r === 'string');
  } catch {
    return [];
  }
}
